import { Module } from "vuex";
import * as firebase from "firebase/app";
import "firebase/firestore";
import router from "@/router";
import moment from "moment";
import { serializer } from "@/api/shared/serializer";
import { EventsTypes } from "@/enums/EventsTypes";
import { UserTypeEnum } from "@/enums/UserTypeEnum";
import { AuditLogsTier1 as Tier1, AuditLogsTier2 as Tier2, AuditLogsTier3 as Tier3 } from '@/enums/AuditLogs';

const socialClubModule: Module<any, any> = {
  namespaced: false,
  state: {
    socialClubs: [],
    socialClubsDropdown: [],
    currentSocialClub: { name: "" },
    currentSocialClubPastEvents: [],
    currentSocialClubUpcomingEvents: [],
    lifestyleProgramCategories: []
  },

  mutations: {
    setSocialClubs(state: any, payload: []) {
      state.socialClubs = payload;
    },
    setSocialClubsDropdown(state: any, payload: []) {
      state.socialClubsDropdown = payload;
    },
    setCurrentSocialClub(state: any, payload: []) {
      state.currentSocialClub = payload;
    },
    setCurrentSocialClubPastEvents(state: any, payload: []) {
      state.currentSocialClubPastEvents = payload;
    },
    setCurrentSocialClubUpcomingEvents(state: any, payload: []) {
      state.currentSocialClubUpcomingEvents = payload;
    },
    setLifestyleProgramCategories(state: any, payload: object) {
      state.lifestyleProgramCategories = payload;
    },
  },

  actions: {
    async getSocialClubs({ commit }) {
      const socialClubs = firebase
        .firestore()
        .collection("socialClubs")
        .where("facilityId", "==", this.state.authModule.currentFacility);

      const categories = (await firebase.firestore().collection("lifestyleProgramCategories")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .get()).docs.map((x: any) => ({ id: x.id, ...x.data() }))

      await socialClubs.get().then((querySnapshot) => {
        const socClubs: Array<any> = [];

        querySnapshot.docs.forEach((x) => {
          const socClub = x.data();
          const category = categories.find(c => c.id == socClub.lifestyleProgramCategoryId)
          socClub.id = x.id;
          socClub.category = category ? category.name : null
          socClubs.push(socClub);
        });
        commit("setSocialClubs", socClubs);
      });
    },
    async getSocialClubList({ commit }) {
      const socialClubs = firebase
        .firestore()
        .collection("socialClubs")
        .where("facilityId", "==", this.state.authModule.currentFacility);

      await socialClubs.get().then((querySnapshot) => {
        const socClubs: Array<any> = [];
        querySnapshot.docs.forEach((x) => {
          const socClub = x.data();
          socClub.id = x.id;
          socClubs.push(socClub);
        });
        const all = { name: "All" };
        socClubs.push(all);

        commit("setSocialClubsDropdown", socClubs);
      });
    },
    async getCurrentSocialProgramEvents({ commit }, { socialClubId }) {

      if (!socialClubId) return;

      const fromDate = moment()
        .toDate();

      const toDate = moment()
        .add(2, "weeks")
        .toDate();

      const pastDate = moment()
        .add(-2, "weeks")
        .toDate();

      const upcomingCalenderEvents = await firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("invitees", "array-contains", socialClubId)
        .orderBy("start", "asc")
        .where("start", ">", fromDate)
        .where("start", "<", toDate).get()

      const pastCalenderEvents = await firebase
        .firestore()
        .collection("calender")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .where("invitees", "array-contains", socialClubId)
        .orderBy("start", "asc")
        .where("start", "<", fromDate)
        .where("start", ">", pastDate).get()

      const upcomingDocs = upcomingCalenderEvents.docs.map(x => {
        const thisRes = x.data();
        const start = moment(thisRes.start.seconds * 1000).toDate();
        thisRes.id = x.id;
        thisRes.start = start;
        thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
        return thisRes
      })

      const pastDocs = pastCalenderEvents.docs.map(x => {
        const thisRes = x.data();
        const start = moment(thisRes.start.seconds * 1000).toDate();
        thisRes.id = x.id;
        thisRes.start = start;
        thisRes.end = moment(thisRes.end.seconds * 1000).toDate();
        return thisRes
      })

      commit("setCurrentSocialClubPastEvents", pastDocs);
      commit("setCurrentSocialClubUpcomingEvents", upcomingDocs);
    },
    async getSocialClub({ commit }, id) {
      const socialClubs = firebase
        .firestore()
        .collection("socialClubs")
        .doc(id);

      await socialClubs.get().then((querySnapshot) => {
        const currentSoc = querySnapshot.data();
        if (currentSoc != null) {
          currentSoc.id = querySnapshot.id;
          commit("setCurrentSocialClub", currentSoc);
        }
      });
    },
    async getLifestyleProgramsReport({ commit }, { startDate, endDate }) {

      const lifestyleProgramCategories =  serializer.querySnapshotToDocuments(await firebase.firestore().collection("lifestyleProgramCategories").where("facilityId", "==", this.state.authModule.currentFacility).get())
      const programs: any[] =  serializer.querySnapshotToDocuments(await firebase.firestore().collection("socialClubs")
      .where("facilityId", "==", this.state.authModule.currentFacility).get())

      const events: any[] = serializer.querySnapshotToDocuments(await firebase
      .firestore()
      .collection("calender")
      .where("facilityId", "==", this.state.authModule.currentFacility)
      .where("type", "==", EventsTypes.LifestylePrograms)
      .where("start", ">", startDate)
      .where("start", "<", endDate).get()).filter((x: any)=>x.invitees.length)
      .map((event: any) => {
        event.galleryPhotosCount = event.galleryPhotos.length
        return event;
      });

      const categories = []

      programs.forEach((program)=>{
        const pEvents: any[] = events.filter((e)=>e.invitees.includes(program.id))
        program.scheduledEventsCount = pEvents.length
        const attendeesEvents = pEvents.filter(x=>x.attendeesIds?.length)
        program.avgAttendeesCount = Math.round(attendeesEvents.length > 0 ? ((attendeesEvents.reduce((sum, event) => sum + event.attendeesIds.length, 0)) / attendeesEvents.length): 0)
        program.picturesCount = pEvents.reduce((sum, event) => sum + event.galleryPhotosCount, 0)
        
        // Calculate separate like types:
        // 1. Event-level likes (likes on the event itself)
        const eventLikesCount = pEvents.reduce((total, event) => {
          return total + (event.likes?.length || 0);
        }, 0);
        
        // 2. Gallery-level likes (likes on the event's photo gallery/album)
        const galleryLikesCount = pEvents.reduce((total, event) => {
          return total + (event.galleryLikes?.length || 0);
        }, 0);
        
        // Assign separate like counts
        program.eventLikesCount = eventLikesCount;
        program.galleryLikesCount = galleryLikesCount;
        // Keep the old likesCount for backward compatibility (total of both)
        program.likesCount = eventLikesCount + galleryLikesCount

        const category = categories.find(x=>x.id === program.lifestyleProgramCategoryId)
        const dbCategory: any = lifestyleProgramCategories.find(x=>x.id === program.lifestyleProgramCategoryId)

        if(category && dbCategory){
          category.programsCount += 1;
        }
        else if(dbCategory){
          categories.push({id: program.lifestyleProgramCategoryId,  name: dbCategory?.name || program?.category, programsCount: 1, colorCode: dbCategory?.color || ''})
        }
      })

      const report = {
        programs: [...programs].sort((a: any, b: any) => b.scheduledEventsCount - a.scheduledEventsCount),
        categories: [...categories].sort((a: any, b: any) => b.programsCount - a.programsCount),
        mostPopularPrograms: [...programs].sort((a: any, b: any) => b.avgAttendeesCount - a.avgAttendeesCount),
        leastPopularPrograms: [...programs].sort((a: any, b: any) => a.avgAttendeesCount - b.avgAttendeesCount),
      }

      return report
    },
    async createNewSocialProgram({ dispatch }, newProgram) {
      newProgram.facilityId = this.state.authModule.currentFacility
      newProgram.createdById = this.state.authModule.user.id
      newProgram.createdByType = UserTypeEnum.Staff
      newProgram.typesOfRecordChanged = [`${Tier1.LifestylePrograms}: ${Tier2.Lifestyle}: ${Tier3.Add}`]
      firebase
        .firestore()
        .collection("socialClubs")
        .add({ ...newProgram })
        .then(() => {
          dispatch("getSocialClubs");
          router.push("/lifestyle-programs/");
        });
    },
    async updateSocialProgram({ commit, dispatch }, program) {
      await firebase
        .firestore()
        .collection("socialClubs")
        .doc(program.id)
        .update({
          videoId: program.videoId,
          name: program.name,
          subscriptionFeePerDay: program.subscriptionFeePerDay || null,
          subscriptionPeriod: program.subscriptionPeriod || null,
          imageUrl: program.imageUrl,
          thumbnailUrl: program?.thumbnailUrl || null,
          description: program.description,
          price: program.price || null,
          type: program.type || null,
          lifestyleProgramCategoryId: program?.lifestyleProgramCategoryId || null,
          lifestyleProgramCategoryName: program?.lifestyleProgramCategoryName || null,
          updatedById: this.state.authModule.user.id,
          updatedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.LifestylePrograms}: ${Tier2.LifestyleProgram}: ${Tier3.Amend}`]
        });
    },
    async deleteSocialProgram({ commit, dispatch }, program) {
      const socialProgramRef = await firebase.firestore().collection("socialClubs").doc(program.id).get()

      await socialProgramRef.ref.update({
        deletedById: this.state.authModule.user.id,
        deletedByType: UserTypeEnum.Staff,
        typesOfRecordChanged: [`${Tier1.LifestylePrograms}: ${Tier2.Lifestyle}: ${Tier3.Remove}`]
      });

      await socialProgramRef.ref.delete();
    },
    async cleanLifeStyleProgramsMembers() {
      const socialClubs = firebase.firestore().collection("socialClubs");

      socialClubs
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .get()
        .then((snapshots) => {
          if (snapshots.size > 0) {
            snapshots.forEach((socialClub) => {
              socialClubs.doc(socialClub.id).update({
                residentsPendingMembershipApproval: [],
                members: [],
                membersNames: [],
              });
            });
          }
        });
    },
    async addLifestyleProgramCategory({ dispatch }, socialProgramCategory) {

      return await firebase
        .firestore()
        .collection("lifestyleProgramCategories")
        .add({
          name: socialProgramCategory.name,
          color: socialProgramCategory.color,
          facilityId: this.state.authModule.currentFacility,
          createdDateTime: firebase.firestore.Timestamp.fromDate(new Date()),
          createdById: this.state.authModule.user.id,
          createdByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Dimension}`]
        })
        .then(async (querySnapshot) => {
          await dispatch("getLifestyleProgramCategories");
          return querySnapshot.id;
        });
    },
    async updateLifestyleProgramCategory({ dispatch }, socialProgramCategory) {
      if (socialProgramCategory.facilityId != this.state.authModule.currentFacility) throw Error('Unable to update social program category')

      return await firebase
        .firestore()
        .collection("lifestyleProgramCategories")
        .doc(socialProgramCategory.id)
        .update({
          name: socialProgramCategory.name,
          isDefault: socialProgramCategory.isDefault || false,
          facilityId: socialProgramCategory.facilityId,
          color: socialProgramCategory.color,
          updatedDateTime: firebase.firestore.Timestamp.fromDate(new Date()),
          updatedById: this.state.authModule.user.id,
          updatedByType: UserTypeEnum.Staff,
          typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Dimension}`]
        })
        .then(async () => {
          return await dispatch("getLifestyleProgramCategories")
        });
    },
    async deleteLifestyleProgramCategory({ dispatch }, id) {

      const socialProgramCategory = await firebase.firestore().collection("lifestyleProgramCategories").doc(id).get()

      if (!socialProgramCategory.exists || socialProgramCategory.data().facilityId != this.state.authModule.currentFacility) throw Error('Unable to delete social program category')

      await socialProgramCategory.ref.update({
        deletedById: this.state.authModule.user.id,
        deletedByType: UserTypeEnum.Staff,
        typesOfRecordChanged: [`${Tier1.StaffHubSettings}: ${Tier2.StaffSettings}: ${Tier3.Dimension}`]
      })
        
      return await socialProgramCategory.ref.delete().then(async () => {
        return await dispatch("getLifestyleProgramCategories")
      });
    },
    async getLifestyleProgramCategories({ commit }) {
      const lifestyleProgramCategories = firebase
        .firestore()
        .collection("lifestyleProgramCategories")
        .where("facilityId", "==", this.state.authModule.currentFacility)
        .orderBy("createdDateTime", "desc");

      return await lifestyleProgramCategories.get().then(querySnapshot => {
        const resList = querySnapshot.docs.map(x => ({ id: x.id, ...x.data() }))
        commit("setLifestyleProgramCategories", resList);
        return resList;
      });
    },
    clearCurrentSocialProgramEvents({ commit }) {
      commit("setCurrentSocialClubPastEvents", null);
      commit("setCurrentSocialClubUpcomingEvents", null);
    },
  },
};
export default socialClubModule;
